// src/tabs/RampaSubTab.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Flame, Clock, Thermometer, Plus, Trash2, ArrowUp, ArrowDown, Save, HelpCircle, Settings, Edit3 } from "lucide-react";
import { pb } from "../services/pocketbase";

export default function RampaSubTab({ user }) {
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipeId, setSelectedRecipeId] = useState("");
  const [etapas, setEtapas] = useState([]);
  const [observacoes, setObservacoes] = useState("");
  const [isEditingRecipe, setIsEditingRecipe] = useState(false);
  const [newRecipeName, setNewRecipeName] = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingRecipeId, setEditingRecipeId] = useState(null);

  // Load recipes
  const loadRecipes = useCallback(async () => {
    try {
      const records = await pb.collection("rampas_receitas").getFullList({ sort: "nome" });
      setRecipes(records);
      if (records.length > 0) {
        const lastSelected = localStorage.getItem("rampa_recipe_id");
        if (lastSelected && records.some(r => r.id === lastSelected)) {
          setSelectedRecipeId(lastSelected);
        } else {
          setSelectedRecipeId(records[0].id);
        }
      }
    } catch (e) {
      console.error("Erro loading rampa recipes:", e);
    }
  }, []);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  // Handle selected recipe changed
  useEffect(() => {
    if (selectedRecipeId) {
      localStorage.setItem("rampa_recipe_id", selectedRecipeId);
      const active = recipes.find(r => r.id === selectedRecipeId);
      if (active) {
        setEtapas(active.etapas ? [...active.etapas].sort((a, b) => a.ordem - b.ordem) : []);
        setObservacoes(active.observacoes || "");
        setIsEditingRecipe(true);
      }
    }
  }, [selectedRecipeId, recipes]);

  const activeRecipe = recipes.find(r => r.id === selectedRecipeId);

  // Stats
  const stats = useMemo(() => {
    const duracao = etapas.reduce((sum, item) => sum + (Number(item.minutos) || 0), 0);
    const tempMax = etapas.reduce((max, item) => Math.max(max, Number(item.temperatura) || 0), 20);
    return {
      duracaoTotalMinutos: duracao,
      duracaoTotalTexto: `${Math.floor(duracao / 60)}h ${duracao % 60}min`,
      temperaturaMaxima: tempMax,
      numeroEtapas: etapas.length
    };
  }, [etapas]);

  // Auto-save was removed to allow manual saving and prevent desynchronization

  // Stage editor actions
  const handleAddEtapa = () => {
    setEtapas(prev => {
      const nextOrdem = prev.length + 1;
      const lastTemp = prev.length > 0 ? prev[prev.length - 1].temperatura : 150;
      return [
        ...prev,
        { id: Date.now().toString(), temperatura: Number(lastTemp), minutos: 60, ordem: nextOrdem }
      ];
    });
  };

  const handleRemoveEtapa = (id) => {
    setEtapas(prev => prev.filter(e => e.id !== id).map((e, idx) => ({ ...e, ordem: idx + 1 })));
  };

  const handleEtapaFieldChange = (id, field, value) => {
    setEtapas(prev =>
      prev.map(e => (e.id === id ? { ...e, [field]: Number(value) || 0 } : e))
    );
  };

  const handleMove = (index, dir) => {
    if (dir === "up" && index === 0) return;
    if (dir === "down" && index === etapas.length - 1) return;
    
    const targetIdx = dir === "up" ? index - 1 : index + 1;
    const nextEtapas = [...etapas];
    const temp = nextEtapas[index];
    nextEtapas[index] = nextEtapas[targetIdx];
    nextEtapas[targetIdx] = temp;

    // Recalcular ordens
    setEtapas(nextEtapas.map((e, idx) => ({ ...e, ordem: idx + 1 })));
  };

  // Create new custom recipe
  // Open modal to edit current recipe name
  const handleOpenEditRecipe = () => {
    if (!activeRecipe) return;
    setEditingRecipeId(activeRecipe.id);
    setNewRecipeName(activeRecipe.nome);
    setShowNewModal(true);
  };

  // Create or update recipe
  const handleCreateOrUpdateRecipe = async (e) => {
    e.preventDefault();
    const cleanName = newRecipeName.trim();
    if (!cleanName) return;

    try {
      if (editingRecipeId) {
        const record = await pb.collection("rampas_receitas").update(editingRecipeId, {
          nome: cleanName
        });
        setRecipes(prev => prev.map(r => r.id === editingRecipeId ? record : r));
        alert(`Perfil "${cleanName}" atualizado!`);
      } else {
        const record = await pb.collection("rampas_receitas").create({
          nome: cleanName,
          tipo: "customizado",
          observacoes: "Perfil de queima personalizado.",
          etapas: [
            { id: "1", temperatura: 150, minutos: 60, ordem: 1 },
            { id: "2", temperatura: 350, minutos: 60, ordem: 2 },
            { id: "3", temperatura: 730, minutos: 120, ordem: 3 }
          ]
        });

        setRecipes(prev => [...prev, record].sort((a,b) => a.nome.localeCompare(b.nome)));
        setSelectedRecipeId(record.id);
        alert(`Perfil "${cleanName}" criado! Edite as etapas abaixo.`);
      }
      setNewRecipeName("");
      setEditingRecipeId(null);
      setShowNewModal(false);
    } catch (err) {
      alert("Erro ao salvar perfil de queima: " + err.message);
    }
  };

  // Delete recipe
  const handleDeleteRecipe = async () => {
    if (!activeRecipe) return;
    if (!window.confirm(`Tem certeza que deseja apagar o perfil "${activeRecipe.nome}"?`)) return;

    try {
      await pb.collection("rampas_receitas").delete(activeRecipe.id);
      const nextRecipes = recipes.filter(r => r.id !== activeRecipe.id);
      setRecipes(nextRecipes);
      if (nextRecipes.length > 0) {
        setSelectedRecipeId(nextRecipes[0].id);
      } else {
        setSelectedRecipeId("");
      }
      alert("Perfil removido com sucesso!");
    } catch (err) {
      alert("Erro ao apagar perfil: " + err.message);
    }
  };

  // Save recipe stage edits manually
  const handleSaveRecipe = async () => {
    if (!selectedRecipeId || !activeRecipe) return;
    try {
      const record = await pb.collection("rampas_receitas").update(selectedRecipeId, {
        etapas: etapas.map((e, idx) => ({ ...e, ordem: idx + 1 })),
        observacoes
      });
      // Update local recipes list to keep in sync
      setRecipes(prev => prev.map(r => r.id === selectedRecipeId ? record : r));
      alert(`Perfil "${activeRecipe.nome}" salvo com sucesso!`);
    } catch (err) {
      alert("Erro ao salvar perfil de queima: " + err.message);
    }
  };

  const handleSaveHistory = async () => {
    if (!activeRecipe) return;
    try {
      await pb.collection("calculos_rampa").create({
        receita_id: activeRecipe.id,
        receita_nome: activeRecipe.nome,
        resultado: {
          temperatura_maxima: stats.temperaturaMaxima,
          duracao_total_minutos: stats.duracaoTotalMinutos,
          numero_etapas: stats.numeroEtapas
        }
      });
      alert("Configuração de queima salva no histórico!");
    } catch (e) {
      alert("Erro ao salvar histórico: " + e.message);
    }
  };

  // --- SVG PLOTTING COMPUTATIONS ---
  const svgData = useMemo(() => {
    if (etapas.length === 0) return null;

    const w = 600;
    const h = 280;
    const padL = 60;
    const padR = 40;
    const padT = 30;
    const padB = 40;

    const totalDur = stats.duracaoTotalMinutos || 60;
    const maxTemp = stats.temperaturaMaxima || 100;
    const graphMaxTemp = Math.ceil((maxTemp + 50) / 100) * 100; // Arredonda temp para grade limpa

    // Build timeline points
    // Ponto inicial padrão: Temp ambiente de 20°C no tempo 0
    const points = [{ x: 0, y: 20, isPlateau: false }];
    let accumTime = 0;
    
    etapas.forEach((et, idx) => {
      accumTime += et.minutos;
      const prevTemp = idx === 0 ? 20 : etapas[idx - 1].temperatura;
      const isPlat = et.temperatura === prevTemp && et.minutos > 0;
      points.push({
        x: accumTime,
        y: et.temperatura,
        isPlateau: isPlat,
        etapaIdx: idx
      });
    });

    // Converter para coordenadas SVG
    const plotPoints = points.map(pt => {
      const xPct = pt.x / totalDur;
      const yPct = pt.y / graphMaxTemp;
      return {
        svgX: padL + xPct * (w - padL - padR),
        svgY: (h - padB) - yPct * (h - padB - padT),
        time: pt.x,
        temp: pt.y,
        isPlateau: pt.isPlateau,
        etapaIdx: pt.etapaIdx
      };
    });

    // Mapear patamares e rampas para linhas
    const lines = [];
    for (let i = 1; i < plotPoints.length; i++) {
      lines.push({
        x1: plotPoints[i - 1].svgX,
        y1: plotPoints[i - 1].svgY,
        x2: plotPoints[i].svgX,
        y2: plotPoints[i].svgY,
        isPlateau: plotPoints[i].isPlateau,
        temp: plotPoints[i].temp,
        duration: plotPoints[i].time - plotPoints[i - 1].time
      });
    }

    // Gerar grade Y (Temperatura)
    const yGridTicks = [];
    const divisionsY = 5;
    for (let i = 0; i <= divisionsY; i++) {
      const tempVal = Math.round((graphMaxTemp / divisionsY) * i);
      const yPct = tempVal / graphMaxTemp;
      const svgY = (h - padB) - yPct * (h - padB - padT);
      yGridTicks.push({ val: tempVal, svgY });
    }

    // Gerar grade X (Tempo)
    const xGridTicks = [];
    const divisionsX = 4;
    for (let i = 0; i <= divisionsX; i++) {
      const timeVal = Math.round((totalDur / divisionsX) * i);
      const xPct = timeVal / totalDur;
      const svgX = padL + xPct * (w - padL - padR);
      xGridTicks.push({ val: timeVal, svgX });
    }

    return {
      width: w,
      height: h,
      padL,
      padR,
      padT,
      padB,
      plotPoints,
      lines,
      yGrid: yGridTicks,
      xGrid: xGridTicks
    };
  }, [etapas, stats]);

  return (
    <div className="flex-1 flex flex-col md:flex-row h-full overflow-hidden bg-slate-50">
      
      {/* LEFT: GRAPH AND EDITOR */}
      <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-6">
        
        {/* RECIPE SELECTION & GENERAL METRICS */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Flame className="text-red-500 animate-pulse" /> Rampa de Queima
              </h2>
              <p className="text-xs text-slate-500">Curvas de aquecimento controlado para desceragem e sinterização</p>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEditingRecipeId(null);
                  setNewRecipeName("");
                  setShowNewModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1"
              >
                <Plus size={14} /> Novo Perfil
              </button>
              {activeRecipe && (
                <>
                  <button
                    onClick={handleOpenEditRecipe}
                    className="bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                  >
                    Editar
                  </button>
                  <button
                    onClick={handleDeleteRecipe}
                    className="bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                    title="Apagar Perfil"
                  >
                    <Trash2 size={14} /> Excluir
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                Selecione a Rampa
              </label>
              <select
                value={selectedRecipeId}
                onChange={(e) => setSelectedRecipeId(e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 font-bold bg-white cursor-pointer"
              >
                {recipes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nome} {r.tipo === "padrao" ? "(Padrão)" : "(Customizado)"}
                  </option>
                ))}
              </select>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200/60 flex flex-col justify-center">
              <span className="text-[9px] font-bold text-slate-500 uppercase block">Duração Total</span>
              <span className="text-sm font-extrabold text-slate-800 flex items-center gap-1 mt-0.5">
                <Clock size={14} className="text-slate-400" /> {stats.duracaoTotalTexto}
              </span>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200/60 flex flex-col justify-center">
              <span className="text-[9px] font-bold text-slate-500 uppercase block">Temp. Máxima</span>
              <span className="text-sm font-extrabold text-slate-800 flex items-center gap-1 mt-0.5">
                <Thermometer size={14} className="text-slate-400" /> {stats.temperaturaMaxima}°C
              </span>
            </div>
          </div>
        </div>

        {/* CHART VISUALIZER */}
        <div className="bg-slate-900 rounded-2xl p-6 shadow-md border border-slate-800 flex flex-col items-center">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 self-start flex items-center gap-1.5">
            Visualizador Gráfico da Curva
          </h3>

          {svgData ? (
            <svg
              viewBox={`0 0 ${svgData.width} ${svgData.height}`}
              className="w-full max-w-full h-auto bg-slate-950/40 rounded-xl border border-slate-800/80 p-2 shadow-inner"
            >
              {/* Grid Lines Y (Temp) */}
              {svgData.yGrid.map((tick, idx) => (
                <g key={`y-${idx}`}>
                  <line
                    x1={svgData.padL}
                    y1={tick.svgY}
                    x2={svgData.width - svgData.padR}
                    y2={tick.svgY}
                    stroke="#1e293b"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                  <text
                    x={svgData.padL - 10}
                    y={tick.svgY + 4}
                    fill="#64748b"
                    fontSize="9"
                    fontWeight="bold"
                    textAnchor="end"
                    className="font-mono"
                  >
                    {tick.val}°C
                  </text>
                </g>
              ))}

              {/* Grid Lines X (Time) */}
              {svgData.xGrid.map((tick, idx) => (
                <g key={`x-${idx}`}>
                  <line
                    x1={tick.svgX}
                    y1={svgData.padT}
                    x2={tick.svgX}
                    y2={svgData.height - svgData.padB}
                    stroke="#1e293b"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                  <text
                    x={tick.svgX}
                    y={svgData.height - svgData.padB + 15}
                    fill="#64748b"
                    fontSize="9"
                    fontWeight="bold"
                    textAnchor="middle"
                    className="font-mono"
                  >
                    {tick.val} min
                  </text>
                </g>
              ))}

              {/* Shaded Area Under Curve */}
              {(() => {
                if (svgData.plotPoints.length < 2) return null;
                const pathCoords = [
                  `M ${svgData.padL} ${svgData.height - svgData.padB}`,
                  ...svgData.plotPoints.map(p => `L ${p.svgX} ${p.svgY}`),
                  `L ${svgData.plotPoints[svgData.plotPoints.length - 1].svgX} ${svgData.height - svgData.padB}`,
                  "Z"
                ].join(" ");

                return (
                  <path
                    d={pathCoords}
                    fill="url(#gradient-queima)"
                    opacity="0.15"
                  />
                );
              })()}

              {/* Gradient Definition */}
              <defs>
                <linearGradient id="gradient-queima" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f43f5e" />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Lines (Rampas e Patamares) */}
              {svgData.lines.map((ln, idx) => (
                <line
                  key={`line-${idx}`}
                  x1={ln.x1}
                  y1={ln.y1}
                  x2={ln.x2}
                  y2={ln.y2}
                  stroke={ln.isPlateau ? "#38bdf8" : "#f43f5e"}
                  strokeWidth={ln.isPlateau ? "4.5" : "3"}
                  strokeLinecap="round"
                />
              ))}

              {/* Nodes and Labels */}
              {svgData.plotPoints.map((pt, idx) => (
                <g key={`node-${idx}`}>
                  <circle
                    cx={pt.svgX}
                    cy={pt.svgY}
                    r={pt.isPlateau ? "5" : "4"}
                    fill={pt.isPlateau ? "#38bdf8" : "#f43f5e"}
                    stroke="#0f172a"
                    strokeWidth="1.5"
                  />
                  {idx > 0 && (
                    <text
                      x={pt.svgX}
                      y={pt.svgY - 10}
                      fill={pt.isPlateau ? "#7dd3fc" : "#fca5a5"}
                      fontSize="9"
                      fontWeight="black"
                      textAnchor="middle"
                      className="font-mono bg-slate-900"
                    >
                      {pt.temp}°C
                    </text>
                  )}
                </g>
              ))}

              {/* X and Y Axis Borders */}
              <line
                x1={svgData.padL}
                y1={svgData.height - svgData.padB}
                x2={svgData.width - svgData.padR}
                y2={svgData.height - svgData.padB}
                stroke="#334155"
                strokeWidth="1.5"
              />
              <line
                x1={svgData.padL}
                y1={svgData.padT}
                x2={svgData.padL}
                y2={svgData.height - svgData.padB}
                stroke="#334155"
                strokeWidth="1.5"
              />
            </svg>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-500 font-bold text-xs">
              Adicione etapas no painel lateral para construir o gráfico.
            </div>
          )}

          <div className="flex gap-4 mt-3 text-[10px] font-bold">
            <div className="flex items-center gap-1 text-red-400">
              <span className="w-2.5 h-1 bg-red-500 inline-block rounded-full"></span> Rampa (Aquecendo)
            </div>
            <div className="flex items-center gap-1 text-sky-400">
              <span className="w-2.5 h-1 bg-sky-400 inline-block rounded-full"></span> Patamar (Mantendo)
            </div>
          </div>
        </div>

        {/* Action Bottom */}
        <div className="flex gap-3">
          <button
            onClick={handleSaveHistory}
            disabled={etapas.length === 0}
            className="flex-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 font-bold p-3.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 text-sm disabled:opacity-50"
          >
            <Save size={18} /> Salvar Histórico da Queima
          </button>
        </div>
      </div>

      {/* RIGHT: EDITABLE LIST OF STAGES */}
      <div className="w-full md:w-[420px] border-t md:border-t-0 md:border-l border-slate-200 bg-white flex flex-col shrink-0">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
          <h3 className="font-bold text-slate-700 flex items-center gap-1.5 text-sm">
            <Flame size={16} className="text-red-500" /> Etapas do Perfil
          </h3>
          {isEditingRecipe ? (
            <div className="flex gap-2">
              <button
                onClick={handleSaveRecipe}
                className="text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-lg hover:bg-emerald-100 transition-all shadow-sm flex items-center gap-1"
              >
                <Save size={12} /> Salvar
              </button>
              <button
                onClick={handleAddEtapa}
                className="text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg hover:bg-blue-100 transition-all shadow-sm"
              >
                + Adicionar
              </button>
            </div>
          ) : (
            <span className="text-[10px] font-extrabold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full uppercase tracking-wider">
              Somente Leitura
            </span>
          )}
        </div>

        {/* LIST CONTAINER */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3">
          {etapas.length > 0 ? (
            etapas.map((et, idx) => {
              const prevTemp = idx === 0 ? 20 : etapas[idx - 1].temperatura;
              const isPlateau = et.temperatura === prevTemp;

              return (
                <div
                  key={et.id}
                  className={`p-3 rounded-xl border flex flex-col gap-3 transition-all relative ${
                    isPlateau
                      ? "bg-sky-50/40 border-sky-200"
                      : "bg-white border-slate-200 shadow-sm"
                  }`}
                >
                  {/* Header info */}
                  <div className="flex justify-between items-center">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      isPlateau ? "bg-sky-100 text-sky-800" : "bg-red-50 text-red-700"
                    }`}>
                      Etapa {idx + 1} — {isPlateau ? "Patamar" : "Rampa"}
                    </span>

                    {isEditingRecipe && (
                      <div className="flex items-center gap-1.5">
                        <button
                          disabled={idx === 0}
                          onClick={() => handleMove(idx, "up")}
                          className="p-1 rounded bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-30"
                          title="Mover para cima"
                        >
                          <ArrowUp size={10} />
                        </button>
                        <button
                          disabled={idx === etapas.length - 1}
                          onClick={() => handleMove(idx, "down")}
                          className="p-1 rounded bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-30"
                          title="Mover para baixo"
                        >
                          <ArrowDown size={10} />
                        </button>
                        <button
                          onClick={() => handleRemoveEtapa(et.id)}
                          className="p-1 rounded bg-red-50 text-red-500 hover:bg-red-100/80 hover:text-red-700 border border-red-200"
                          title="Excluir Etapa"
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Inputs */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                        Temperatura Alvo
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="1100"
                          disabled={!isEditingRecipe}
                          value={et.temperatura}
                          onChange={(e) => handleEtapaFieldChange(et.id, "temperatura", e.target.value)}
                          className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono font-bold text-xs"
                          placeholder="Ex: 700"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400">°C</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                        Duração da Etapa
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          disabled={!isEditingRecipe}
                          value={et.minutos}
                          onChange={(e) => handleEtapaFieldChange(et.id, "minutos", e.target.value)}
                          className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono font-bold text-xs"
                          placeholder="Ex: 90"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400">min</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-8 text-center text-slate-400">
              <HelpCircle size={36} className="mb-2 text-slate-300" />
              <span className="text-xs font-semibold">Sem etapas no perfil selecionado.</span>
            </div>
          )}
        </div>

        {/* Obs Area */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 shrink-0">
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
            Notas / Observações
          </label>
          <textarea
            value={observacoes}
            disabled={!isEditingRecipe}
            onChange={(e) => setObservacoes(e.target.value)}
            className="w-full p-2.5 border border-slate-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-500 h-16 bg-white"
            placeholder="Opcional..."
          />
        </div>
      </div>

      {/* NEW/EDIT PROFILE NAME MODAL */}
      {showNewModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-800">
                {editingRecipeId ? "Editar Nome do Perfil" : "Criar Novo Perfil de Rampa"}
              </h3>
              <button onClick={() => { setShowNewModal(false); setEditingRecipeId(null); }} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleCreateOrUpdateRecipe} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Perfil *</label>
                <input
                  type="text"
                  required
                  value={newRecipeName}
                  onChange={(e) => setNewRecipeName(e.target.value)}
                  placeholder="Ex: Rampa Ouro Rápida"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => { setShowNewModal(false); setEditingRecipeId(null); }}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  {editingRecipeId ? "Salvar Alterações" : "Criar Perfil"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
